import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { NgSelectComponent } from '@ng-select/ng-select';
import { toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleExclamation, faCircleInfo, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { ManagementUser } from '../../../core/models/management-user.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { BulkStudentEditPayload } from '../../../core/models/student.model';

type InputControl =
  | 'updateTeacher'
  | 'updateClass'
  | 'updateSessionTime'
  | 'updatePassword'
  | 'updateExtraSupport';

type ValueControl = keyof BulkStudentEditPayload;

const DATA_CONTROLS = new Map<InputControl, ValueControl>([
  ['updateTeacher', 'teacherId'],
  ['updateClass', 'schoolClassName'],
  ['updateSessionTime', 'sessionLength'],
  ['updatePassword', 'password'],
  ['updateExtraSupport', 'extraSupportNeeded'],
]);

@Component({
  selector: 'wf-student-bulk-edit',
  standalone: true,
  templateUrl: './bulk-edit.component.html',
  styleUrls: ['../../../core/styles/drawer.css', './bulk-edit.component.css'],
  imports: [ReactiveFormsModule, NgSelectComponent, FontAwesomeModule],
})
export class StudentBulkEditComponent {
  private readonly sessionService = inject(SessionStorageService);
  private readonly formBuilder = inject(FormBuilder);
  readonly sessionTimes = signal<number[]>([15, 20, 25, 30]);
  isOpen = input<boolean>(false);
  isSaving = input<boolean>(false);
  saveError = input<string>('');
  sharedTeacherId = input<number | null>(null);
  ssoUser = input<boolean>(false);
  studentIds = input<Set<number>>(new Set());
  teachers = input<ManagementUser[]>([]);
  teacherClasses = input<Map<number, string[]>>(new Map());
  onClose = output<void>();
  onSave = output<BulkStudentEditPayload>();
  dialogElement = viewChild<ElementRef<HTMLDialogElement>>('bulkEditDialog');
  errorIcon = faCircleExclamation ;
  infoIcon = faCircleInfo ;
  loadingIcon = faSpinner ;
  bulkEditForm: FormGroup ;

  readonly availableClasses = computed(() => {
    const classesMap = this.teacherClasses();
    const activeTeacherId = this.updateTeacherValue() ? this.selectedTeacher()?.userID : this.sharedTeacherId();

    if (!activeTeacherId) return [];

    return classesMap.get(activeTeacherId) ?? [];
  });

  readonly hasPropsToUpdate = computed(() => {
    const currentValues = this.formValues();

    return Array.from(DATA_CONTROLS.keys()).some(
      (controlKey) => currentValues[controlKey] === true
    );
  })

  private readonly updateTeacherValue ;
  private readonly selectedTeacher;
  private readonly formValues;

  constructor() {
    // Construct our form based on given input
    const form: Partial<Record<InputControl | Exclude<ValueControl, 'studentIds' | 'clearSchoolClass'>, any>> = {
      updateTeacher: [false],
      updateClass: [false],
      updateSessionTime: [false],
      updateExtraSupport: [false],
      teacherId: [{ value: null, disabled: true }, Validators.required],
      schoolClassName: [{ value: '', disabled: true }],
      sessionLength: [{ value: null, disabled: true }, Validators.required],
      extraSupportNeeded: [{ value: false, disabled: true }],
    };

    if (!this.ssoUser()) {
      form.updatePassword = [false];
      form.password = [{ value: '', disabled: true }, Validators.compose([
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(20),
        Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9_!?]){8,20}$')
      ])];
    }
    this.bulkEditForm = this.formBuilder.group(form);

    // Initialize our signals that we need to be reactive to
    this.updateTeacherValue = toSignal(
      this.bulkEditForm.get('updateTeacher')!.valueChanges,
      { initialValue: false }
    );
    this.selectedTeacher = toSignal(
      this.bulkEditForm.get('teacherId')!.valueChanges,
      { initialValue: null }
    );
    this.formValues = toSignal(
      this.bulkEditForm.valueChanges,
      { initialValue: this.bulkEditForm.value }
    );

    effect(() => {
      const dialog = this.dialogElement()?.nativeElement;
      if (!dialog) return ;

      if (this.isOpen())
      {
        dialog.showModal();
      }
      else
      {
        this.resetForm();
        dialog.close();
      }
    });

    // We only want to allow the Class to be modified if all selected students belong to
    // the same teacher OR the teacher is also being updated for all students
    effect(() => {
      const isSameTeacher = this.sharedTeacherId() !== null;
      const isUpdatingTeacher = this.updateTeacherValue();

      // Logic: Enabled if (Updating Teacher OR Same Teacher)
      const canEditClass = isUpdatingTeacher || isSameTeacher;

      const classToggle = this.bulkEditForm.get('updateClass');
      const classInput = this.bulkEditForm.get('schoolClassName');

      if (canEditClass)
      {
        classToggle?.enable({ emitEvent: false });
      }
      else
      {
        // If we can't edit the class, we must force it to be disabled AND unchecked
        classToggle?.disable({ emitEvent: false });
        classToggle?.setValue(false, { emitEvent: false });

        // Also ensure the actual data input is disabled/reset
        classInput?.disable({ emitEvent: false });
        classInput?.reset('', { emitEvent: false });
      }
    });
  }

  toggleField(controlName: string, inputName: string) {
    const isChecked = this.bulkEditForm.get(controlName)?.value;
    const control = this.bulkEditForm.get(inputName);

    if (isChecked)
    {
      control?.enable();
    }
    else
    {
      control?.disable();
      control?.reset();
    }
  }

  handleCancel() {
    this.onClose.emit();
  }

  handleSave() {
    // First, make sure our form is valid
    if (this.bulkEditForm.invalid) {
      this.bulkEditForm.markAllAsTouched();
      return ;
    }

    const processed: BulkStudentEditPayload = {
      studentIds: [...this.studentIds()],
    };
    const formValues = this.bulkEditForm.value;

    DATA_CONTROLS.forEach((valueKey, toggleKey) => {
      if (formValues[toggleKey] === true) {
        (processed as any)[valueKey] = formValues[valueKey];
      }
    });

    if (this.bulkEditForm.value.updateClass && !this.bulkEditForm.value.schoolClassName) {
      processed['clearSchoolClass'] = true;
    }

    if (Object.keys(processed).length > 0) {
      this.onSave.emit(processed);
    }
  }

  get teacherInput() {
    return this.bulkEditForm.get('teacher');
  }

  get classInput() {
    return this.bulkEditForm.get('schoolClassName');
  }

  get sessionLengthInput() {
    return this.bulkEditForm.get('sessionLength');
  }

  get sessionTimeToggle() {
    return this.bulkEditForm.get('updateSessionTime');
  }

  get extraSupportInput() {
    return this.bulkEditForm.get('extraSupportNeeded');
  }

  get extraSupportToggle() {
    return this.bulkEditForm.get('updateExtraSupport');
  }

  get passwordInput() {
    return this.bulkEditForm.get('password');
  }

  private resetForm() {
    this.bulkEditForm.reset();

    // Uncheck all inputs
    for (const inputId of DATA_CONTROLS.values()) {
      this.bulkEditForm.get(inputId)?.disable();
    }
  }
}
